import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import EMPAccordion from "../../shared/components/EMPAccordion/EMPAccordion";
import EMPDataCard from "../../shared/components/EMPDataCard/EMPDataCard";
import EMPObjectCard from "../../shared/components/EMPDataCard/EMPObjectCard";
import SharedToolTip from "../../shared/sharedToolTip/SharedToolTip";

export const getFinancingHeadingSx = (isLincolnBrand) => ({
  color: "text.primary",
  ...(isLincolnBrand && {
    fontFamily: "ProximaNovaSemibold, Arial, sans-serif",
    fontSize: "24px",
    fontWeight: 600,
    lineHeight: "32px",
  }),
});

const lincolnCardSx = {
  m: 0,
  p: 4,
  border: "1px solid #B2B2B2",
  borderRadius: "8px",
  backgroundColor: "#FFFFFF",
};

export const FinancingCard = ({ data, isLincolnBrand = false, testId }) => {
  if (!isLincolnBrand) {
    return Array.isArray(data) ? (
      <EMPDataCard data={data} />
    ) : (
      <EMPObjectCard data={data} />
    );
  }

  const fields = (
    Array.isArray(data) ? data : Object.values(data || {})
  ).filter((field) => field && field.heading);

  return (
    <Box
      component="dl"
      data-testid={testId}
      sx={{
        ...lincolnCardSx,
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          sm: "repeat(2, minmax(0, 1fr))",
          md: `repeat(${Math.max(1, Math.min(fields.length, 6))}, minmax(0, 1fr))`,
        },
        gap: 4,
        alignItems: "start",
      }}
    >
      {fields.map((field, index) => (
        <Box key={`${field.heading}-${index}`} sx={{ minWidth: 0 }}>
          <Typography component="dt" variant="caption" sx={{ mb: 1 }}>
            {field.heading}{" "}
            {field.hasTooltip && <SharedToolTip body={field.toolTipMessage} />}
          </Typography>
          <Typography
            component="dd"
            variant="body2"
            sx={{ m: 0, overflowWrap: "anywhere" }}
          >
            {field.content ?? "--"}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

FinancingCard.propTypes = {
  data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  isLincolnBrand: PropTypes.bool,
  testId: PropTypes.string,
};

export const FinancingDetailsSection = ({
  children,
  heading,
  isLincolnBrand = false,
  expanded,
  onChange,
}) => {
  if (!isLincolnBrand) {
    return (
      <EMPAccordion heading={heading} expanded={expanded} onChange={onChange}>
        {children}
      </EMPAccordion>
    );
  }

  return (
    <Box component="section" sx={{ mt: 8 }}>
      <Typography variant="h3" sx={{ ...getFinancingHeadingSx(true), mb: 4 }}>
        {heading}
      </Typography>
      <Box
        data-testid="financing-signing-summary"
        sx={{ width: { xs: "100%", md: "50%" } }}
      >
        {children}
      </Box>
    </Box>
  );
};

FinancingDetailsSection.propTypes = {
  children: PropTypes.node,
  heading: PropTypes.string.isRequired,
  isLincolnBrand: PropTypes.bool,
  expanded: PropTypes.bool,
  onChange: PropTypes.func,
};
